// Which GEO_EXEC features this deployment actually has switched on.
//
// The rule from the nav review: a switch that is off must not leave a nav item or a button that
// takes the client to a 404. The server already tells us — every route of a switched-off feature
// answers `404 {"error": "not found"}`, and that exact body is what separates "the feature is not
// deployed" from "that id is not yours" (which answers `brand not found` / `site not found`, i.e.
// the route exists). So the probe is one real read, and its verdict is cached for the tab.
//
// Three states, and "unknown" is not "off": a probe that failed for a transient reason (offline,
// a 500) is NOT cached and NOT treated as a verdict, it is simply retried on the next mount. Only
// a decisive answer is written down. Until a feature is known to be on, its entry stays hidden —
// hiding something that exists is a smaller mistake than offering something that 404s.

import { reactive } from "vue";

/** The body a switched-off GEO_EXEC route returns, verbatim (`_off()` in every geo_exec route). */
const OFF_BODY = "not found";
const STORAGE_PREFIX = "soa_geo_exec_";

export const geoExecFeatures = reactive({});
const inflight = {};

function cached(key) {
  try {
    const value = sessionStorage.getItem(STORAGE_PREFIX + key);
    return value === "on" || value === "off" ? value : "";
  } catch {
    return ""; // private mode: probe every load rather than fail
  }
}

function remember(key, verdict) {
  geoExecFeatures[key] = verdict;
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, verdict);
  } catch {
    /* Storage is optional. */
  }
  return verdict;
}

/** True only when this feature has been confirmed present. Unknown reads as hidden. */
export function featureOn(key) {
  return (geoExecFeatures[key] || cached(key)) === "on";
}

export function featureState(key) {
  return geoExecFeatures[key] || cached(key) || "unknown";
}

/**
 * Probe one feature with a real read of one of its routes. `request` is called at most once per
 * verdict; concurrent callers share the same in-flight promise.
 */
export async function probeFeature(key, request) {
  const known = geoExecFeatures[key] || cached(key);
  if (known) {
    geoExecFeatures[key] = known;
    return known;
  }
  if (inflight[key]) return inflight[key];
  inflight[key] = (async () => {
    try {
      await request();
      return remember(key, "on");
    } catch (error) {
      if (error?.status === 404) {
        return remember(key, String(error?.payload?.error || "") === OFF_BODY ? "off" : "on");
      }
      return "unknown"; // transient: no verdict, so the next mount asks again
    } finally {
      inflight[key] = null;
    }
  })();
  return inflight[key];
}

// ── FE-3 · the same verdicts, for features probed by their first real call ───────────────────
// R10/R11/R18 have no `features` endpoint to probe up front, so the first real read IS the probe
// and these three wrap the primitives above. They differ from `featureOn` in what UNKNOWN means:
// `featureOn` hides until confirmed (a nav entry that 404s is worse than a missing one), while
// these keep an entry point visible until the switch has actually said no — blanking a panel on
// "we have not asked yet" would flicker it away on every cold load.

/** True when this error is the feature switch saying "I am not here", not a bad id. */
export function featureIsOff(error) {
  return Boolean(error) && error.status === 404 && String(error.payload?.error || "") === OFF_BODY;
}

/** Record what one call taught us. Returns true when the error was the switch (so: stop, quietly). */
export function noteFeature(name, error) {
  if (!error) {
    remember(name, "on");
    return false;
  }
  if (!featureIsOff(error)) return false; // a transient failure is not a verdict
  remember(name, "off");
  return true;
}

/** Hidden only once the switch has said no. Unknown stays visible. */
export function featureHidden(name) {
  return featureState(name) === "off";
}
